"use client";

import { useEffect, useRef, useState } from "react";

const stats = [
  { value: 2800, suffix: "+", label: "Campaigns Funded", icon: "💛", color: "#2D6A8F" },
  { value: 12.4, suffix: "M", prefix: "$", label: "Raised for Families", icon: "🌱", color: "#5A8A6F" },
  { value: 94, suffix: "%", label: "Reach Their Goal", icon: "🎯", color: "#E8A598", textColor: "#D4806F" },
  { value: 180, suffix: "+", label: "Partner Clinics", icon: "🏥", color: "#2D6A8F" },
];

function CountUp({ target, suffix = "", prefix = "", duration = 1800 }: {
  target: number; suffix?: string; prefix?: string; duration?: number;
}) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    const isDecimal = target % 1 !== 0;
    const raf = requestAnimationFrame(function tick(now) {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const current = target * eased;
      setCount(isDecimal ? Math.round(current * 10) / 10 : Math.round(current));
      if (progress < 1) requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
  }, [started, target, duration]);

  const display = target % 1 !== 0 ? count.toFixed(1) : count.toString();
  return <span ref={ref}>{prefix}{display}{suffix}</span>;
}

export default function TrustStatsBar() {
  return (
    <section className="py-16 relative" style={{ background: "#FFFFFF" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px rounded-3xl overflow-hidden border border-warm-border shadow-warm-sm">
          {stats.map(({ value, suffix, prefix, label, icon, color, textColor }, i) => (
            <div
              key={label}
              className={`bg-white px-8 py-10 flex flex-col items-center text-center group hover:bg-warm-bg transition-colors duration-300 ${i < stats.length - 1 ? "border-r border-warm-border-light" : ""}`}
            >
              <span className="text-3xl mb-3 animate-heartbeat" style={{ display: "inline-block" }} aria-hidden="true">{icon}</span>
              <div className="text-3xl md:text-4xl font-heading font-bold mb-1.5" style={{ color: textColor || color }}>
                <CountUp target={value} suffix={suffix} prefix={prefix || ""} />
              </div>
              <p className="text-sm text-ink-muted font-medium">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}